import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  api,
  formatApiError,
  setStoredToken,
  clearStoredToken,
  registerPermissionRevalidationHandler,
} from "./api";
import { MODULE_PERMISSION_MAP } from "./permissions";

// ---------------------------------------------------------------------------
// Sprint 1B-B · US 1.5 · client-side freshness constants
// ---------------------------------------------------------------------------
export const PERMISSION_FRESHNESS_MS = 60_000;
export const PERMISSION_POLL_MS = 45_000;
export const PERMISSION_FOCUS_DEBOUNCE_MS = 30_000;

// ---------------------------------------------------------------------------
// Sprint 1B-B · US 1.9-B · resolver-hardening constants (private)
// ---------------------------------------------------------------------------
const MIN_COOLDOWN_MS = 5_000;
const MAX_COOLDOWN_MS = 120_000;
const DEFAULT_COOLDOWN_MS = 30_000;
const TRANSIENT_RETRY_MS = 5_000;
const PERMISSION_RETRY_STORAGE_KEY = "harborwell.permissionRetryUntil";

// ---------------------------------------------------------------------------
// Sprint 1B-B · US 1.9-B · session-storage helpers (deadline-only)
// ---------------------------------------------------------------------------
// The sessionStorage entry stores ONLY the epoch-ms cooldown deadline. No
// tokens, permissions, user info, error bodies, headers, MFA data, cookies,
// or authentication secrets are ever written. Each helper is total (never
// throws) and safe in restrictive browser modes where sessionStorage is
// unavailable or throws on access.

function _sessionStorageAvailable() {
  try {
    if (typeof window === "undefined") return false;
    return !!window.sessionStorage;
  } catch {
    return false;
  }
}

function readStoredRetryUntil() {
  try {
    if (!_sessionStorageAvailable()) return null;
    const raw = window.sessionStorage.getItem(PERMISSION_RETRY_STORAGE_KEY);
    if (raw == null) return null;
    // Canonical positive integer string only. Reject empty, whitespace,
    // fractional, exponent-form, negative, zero, sign-prefixed, and
    // leading-zero-padded values.
    if (typeof raw !== "string" || !/^[1-9]\d{0,15}$/.test(raw)) {
      _bestEffortDelete();
      return null;
    }
    const n = parseInt(raw, 10);
    if (!Number.isSafeInteger(n) || n <= 0) {
      _bestEffortDelete();
      return null;
    }
    const now = Date.now();
    // Expired — remove and treat as absent.
    if (n <= now) {
      _bestEffortDelete();
      return null;
    }
    // Bound: a stored value greater than now + MAX_COOLDOWN_MS is clamped
    // and rewritten to storage before hydrating the ref and reducer. This
    // prevents any indefinite lockout regardless of how the value was set.
    const upperBound = now + MAX_COOLDOWN_MS;
    if (n > upperBound) {
      try { window.sessionStorage.setItem(PERMISSION_RETRY_STORAGE_KEY, String(upperBound)); } catch { /* noop */ }
      return upperBound;
    }
    return n;
  } catch {
    return null;
  }
}

function writeStoredRetryUntil(nextRetryUntil) {
  try {
    if (!_sessionStorageAvailable()) return;
    if (!Number.isSafeInteger(nextRetryUntil) || nextRetryUntil <= 0) return;
    window.sessionStorage.setItem(PERMISSION_RETRY_STORAGE_KEY, String(nextRetryUntil));
  } catch {
    // §3: storage-failure — swallow. In-memory ref+reducer continue to
    // enforce for the provider lifetime. Reload-persistent protection is
    // simply unavailable in this browser session.
  }
}

function _bestEffortDelete() {
  try {
    if (!_sessionStorageAvailable()) return;
    window.sessionStorage.removeItem(PERMISSION_RETRY_STORAGE_KEY);
  } catch {
    // If removeItem throws, attempt a "0" overwrite so the value cannot
    // hydrate to an active deadline on a subsequent mount. Swallow a second
    // failure.
    try {
      if (_sessionStorageAvailable()) {
        window.sessionStorage.setItem(PERMISSION_RETRY_STORAGE_KEY, "0");
      }
    } catch {
      /* noop */
    }
  }
}

function deleteStoredRetryUntil() {
  _bestEffortDelete();
}

// Parse HTTP Retry-After: integer seconds or HTTP-date. Returns a positive
// integer seconds value clamped into [MIN, MAX]. Falls back to DEFAULT for
// missing/invalid input.
function parseRetryAfterSeconds(headerValue) {
  const min = Math.max(1, Math.floor(MIN_COOLDOWN_MS / 1000));
  const max = Math.max(min, Math.floor(MAX_COOLDOWN_MS / 1000));
  const def = Math.min(max, Math.max(min, Math.floor(DEFAULT_COOLDOWN_MS / 1000)));
  if (headerValue == null) return def;
  const s = String(headerValue).trim();
  if (s === "") return def;
  if (/^\d{1,10}$/.test(s)) {
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n < 0) return def;
    return Math.min(max, Math.max(min, n));
  }
  const parsed = Date.parse(s);
  if (Number.isFinite(parsed)) {
    const delta = Math.ceil((parsed - Date.now()) / 1000);
    if (delta > 0) return Math.min(max, Math.max(min, delta));
  }
  return def;
}

// ---------------------------------------------------------------------------
// HC-EPIC-002 · Phase 2 · Slice 2D · permission-Set equality (private)
// ---------------------------------------------------------------------------
// Meaningful-change detector used by refreshPermissions() to decide when
// the current-user record (user.role) needs to be re-fetched from the
// backend. Membership equality only — reference identity is NOT sufficient
// because a new Set is constructed on every /permissions/me success.
//
// Contract:
//   - Returns true when both operands are non-null Sets of equal size and
//     equal membership (order-independent).
//   - Returns true when both operands are the same reference (including
//     both being null).
//   - Returns false when exactly one operand is null/undefined.
//   - Never throws. Never mutates its arguments.
function _permissionSetsEqual(prev, next) {
  if (prev === next) return true;
  if (prev == null || next == null) return false;
  if (typeof prev.size !== "number" || typeof next.size !== "number") return false;
  if (prev.size !== next.size) return false;
  for (const v of prev) {
    if (!next.has(v)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Permission-state reducer
// ---------------------------------------------------------------------------

const INITIAL = {
  permissions: null,
  permissionState: "idle",
  permissionsVerifiedAt: null,
  retryUntil: 0,
  resolverError: null,
};

function permReducer(state, action) {
  switch (action.type) {
    case "REFRESH_STARTED":
      return {
        ...state,
        permissionState: state.permissions ? "refreshing" : "loading",
      };
    case "REFRESH_SUCCESS":
      return {
        permissions: action.permissions,
        permissionState: "ready",
        permissionsVerifiedAt: action.verifiedAt,
        retryUntil: 0,
        resolverError: null,
      };
    case "REFRESH_FAILURE": {
      const stillFresh =
        state.permissionsVerifiedAt != null &&
        action.now - state.permissionsVerifiedAt < PERMISSION_FRESHNESS_MS;
      if (stillFresh) {
        return { ...state, permissionState: "refreshing", resolverError: action.resolverError || null };
      }
      return { ...state, permissionState: "error", resolverError: action.resolverError || null };
    }
    case "REFRESH_FAILURE_401":
      return {
        permissions: null,
        permissionState: "error",
        permissionsVerifiedAt: null,
        retryUntil: 0,
        resolverError: "401",
      };
    case "REFRESH_FAILURE_403":
      return {
        permissions: null,
        permissionState: "error",
        permissionsVerifiedAt: null,
        retryUntil: 0,
        resolverError: "403",
      };
    case "REFRESH_FAILURE_429": {
      // Preserve permissions/verifiedAt so stillFresh grace applies. Fresh
      // consumers continue to see `can()===true` until FRESHNESS_EXPIRED.
      return {
        ...state,
        permissionState:
          state.permissionsVerifiedAt != null &&
          Date.now() - state.permissionsVerifiedAt < PERMISSION_FRESHNESS_MS
            ? "refreshing"
            : "error",
        retryUntil: action.retryUntil,
        resolverError: "429",
      };
    }
    case "COOLDOWN_EXPIRED":
      // A caller reached the cooldown gate and observed natural expiry.
      // Ref+storage have already been zeroed synchronously; this action
      // updates the UI mirror. Leave resolverError untouched so the UI
      // can distinguish "cooldown lifted, still recovering" from
      // "cooldown active".
      return { ...state, retryUntil: 0 };
    case "MOUNT_RESUMED_COOLDOWN":
      // Authoritative mount-time state when a live persisted deadline is
      // hydrated. This runs BEFORE checkSession() dispatches anything and
      // ensures the resolver-failure recovery experience is reachable
      // without issuing /permissions/me.
      return {
        permissions: null,
        permissionState: "error",
        permissionsVerifiedAt: null,
        retryUntil: action.retryUntil,
        resolverError: "429",
      };
    case "FRESHNESS_EXPIRED":
      if (state.permissionState !== "refreshing") return state;
      return { ...state, permissionState: "error" };
    case "INVALIDATE":
      // Preserve cooldown mirror; only permissions/state are invalidated.
      return {
        ...state,
        permissions: null,
        permissionState: "error",
        permissionsVerifiedAt: null,
        resolverError: null,
      };
    case "LOGOUT":
      return { ...INITIAL };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // null = checking, false = anon, object = authed
  const [loading, setLoading] = useState(true);
  const [permState, dispatch] = useReducer(permReducer, INITIAL);

  const mountedRef = useRef(true);
  const genRef = useRef(0);
  const inflightRef = useRef(null);
  const abortRef = useRef(null);
  const focusLastRef = useRef(0);
  const expiryTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  // US 1.9-B: authoritative synchronous cooldown deadline + pending retry.
  const retryUntilRef = useRef(0);
  const pendingRetryRef = useRef(null); // { genAtArm: number, timerId } | null
  // HC-EPIC-002 · Slice 2D: current-user refresh coordination.
  // - initialPermissionsLoadedRef: false until the first successful
  //   /permissions/me completes for this session. Prevents the initial
  //   population (null → Set) from being interpreted as a change and
  //   spuriously firing /auth/me right after checkSession() (which just
  //   set user from /auth/me itself).
  // - userRefreshInflightRef: dedupes concurrent Slice-2D-initiated
  //   /auth/me refreshes so overlapping permission refreshes yield at
  //   most one in-flight /auth/me at any given instant.
  const initialPermissionsLoadedRef = useRef(false);
  const userRefreshInflightRef = useRef(null);

  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const clearPendingPermissionRetry = useCallback(() => {
    if (pendingRetryRef.current) {
      try { clearTimeout(pendingRetryRef.current.timerId); } catch { /* noop */ }
      pendingRetryRef.current = null;
    }
  }, []);

  const permStateRef = useRef(permState);
  permStateRef.current = permState;

  // Forward-declared for use inside the transient-retry timer callback.
  const refreshPermissionsRef = useRef(null);

  const armTransientRetry = useCallback((genAtArm) => {
    // Preconditions checked at call site: !options._isRetry, retryable class,
    // no active cooldown, mountedRef.current, pendingRetryRef.current === null.
    const timerId = setTimeout(() => {
      // First: clear the pending marker so it is null while the retry runs.
      clearPendingPermissionRetry();
      if (!mountedRef.current) return;
      if (genAtArm !== genRef.current) return;                    // superseded lifecycle
      if (retryUntilRef.current > 0 && Date.now() < retryUntilRef.current) return; // 429 arrived meanwhile
      const fn = refreshPermissionsRef.current;
      if (typeof fn === "function") { void fn({ _isRetry: true }); }
    }, TRANSIENT_RETRY_MS);
    pendingRetryRef.current = { genAtArm, timerId };
  }, [clearPendingPermissionRetry]);

  const refreshPermissions = useCallback(async (options = {}) => {
    if (!mountedRef.current) return undefined;
    const { force = false, _isRetry = false } = options;

    // 1. Cooldown gate — authoritative synchronous check via retryUntilRef.
    //    NOTE: force:true DOES NOT bypass this. A blocked refresh MUST NOT
    //    cancel any armed transient-retry timer.
    if (retryUntilRef.current > 0) {
      const now = Date.now();
      if (now < retryUntilRef.current) {
        return inflightRef.current || undefined;
      }
      // Natural expiration — clear synchronously before proceeding.
      deleteStoredRetryUntil();
      retryUntilRef.current = 0;
      dispatch({ type: "COOLDOWN_EXPIRED" });
      // fall through to normal path
    }

    // 2. In-flight dedup / freshness collapse (existing contract).
    if (inflightRef.current && !force) return inflightRef.current;

    // 3. We are about to actually start a new /permissions/me request.
    //    Only NOW do we cancel any armed transient retry.
    clearPendingPermissionRetry();

    const myGen = ++genRef.current;
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    abortRef.current =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    dispatch({ type: "REFRESH_STARTED" });

    const promise = (async () => {
      try {
        const { data } = await api.get("/permissions/me", {
          signal: abortRef.current?.signal,
        });
        if (!mountedRef.current || myGen !== genRef.current) return;
        const perms = new Set(Array.isArray(data?.permissions) ? data.permissions : []);
        const now = Date.now();
        // HC-EPIC-002 · Slice 2D: detect a meaningful permission-set change
        // BEFORE the reducer overwrites the prior set. The very first
        // successful refresh in this session is treated as initial load
        // (not a change) so that we do not issue /auth/me a second time
        // immediately after checkSession() or login() (both of which just
        // populated `user` from /auth/me or /auth/login).
        const _prevPerms = permStateRef.current.permissions;
        const _shouldRefreshUser =
          initialPermissionsLoadedRef.current &&
          !_permissionSetsEqual(_prevPerms, perms);
        initialPermissionsLoadedRef.current = true;
        // Success — clear cooldown state synchronously in one tick.
        deleteStoredRetryUntil();
        retryUntilRef.current = 0;
        clearPendingPermissionRetry();
        dispatch({ type: "REFRESH_SUCCESS", permissions: perms, verifiedAt: now });
        clearExpiryTimer();
        expiryTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          dispatch({ type: "FRESHNESS_EXPIRED" });
        }, PERMISSION_FRESHNESS_MS);
        // HC-EPIC-002 · Slice 2D: issue exactly one /auth/me to synchronize
        // the in-memory `user` object (specifically `user.role`) with the
        // backend when the permission set changed. Dedup against any prior
        // in-flight Slice-2D refresh so overlapping /permissions/me events
        // never fan out. The call is fire-and-forget from the caller's
        // perspective; failures are absorbed silently and DO NOT clear
        // `user`, dispatch LOGOUT, mutate cooldown state, or interfere
        // with permission-state timers.
        if (_shouldRefreshUser && userRefreshInflightRef.current === null) {
          const _meGen = genRef.current;
          const _meRefreshPromise = (async () => {
            try {
              const { data: meData } = await api.get("/auth/me");
              if (!mountedRef.current) return;
              if (_meGen !== genRef.current) return; // superseded lifecycle
              // Preserve locally-set fields (e.g., token bookkeeping from
              // login flow) by merging /auth/me into the prior user object.
              // If the prior state is anonymous (false) or checking (null),
              // do NOT resurrect the user — the session may have ended
              // concurrently and we must never widen access.
              setUser((prevUser) => {
                if (!prevUser || prevUser === false) return prevUser;
                return { ...prevUser, ...meData };
              });
            } catch {
              // Silent failure — preserve `user` as-is. A stale role is
              // strictly no worse than the pre-Slice-2D behavior; the next
              // successful poll will retry the sync automatically.
            } finally {
              if (userRefreshInflightRef.current === _meRefreshPromise) {
                userRefreshInflightRef.current = null;
              }
            }
          })();
          userRefreshInflightRef.current = _meRefreshPromise;
        }
      } catch (e) {
        if (!mountedRef.current || myGen !== genRef.current) return;
        if (e && (e.name === "CanceledError" || e.name === "AbortError")) return;

        const status = e?.response?.status;
        const code = e?.code;
        const hasResponse = !!e?.response;
        const isRetryableStatus = status === 502 || status === 503 || status === 504;
        const isNetworkError = !hasResponse;
        const isTimeout = code === "ECONNABORTED";
        const isNetworkCode = code === "ERR_NETWORK";
        const isRetryableTransient =
          isRetryableStatus || isNetworkError || isTimeout || isNetworkCode;

        if (status === 429) {
          const retryAfter = e?.response?.headers?.["retry-after"];
          const seconds = parseRetryAfterSeconds(retryAfter);
          const nextRetryUntil = Date.now() + seconds * 1000;
          writeStoredRetryUntil(nextRetryUntil);
          retryUntilRef.current = nextRetryUntil;
          clearPendingPermissionRetry();
          dispatch({ type: "REFRESH_FAILURE_429", retryUntil: nextRetryUntil });
          return;
        }
        if (status === 401) {
          deleteStoredRetryUntil();
          retryUntilRef.current = 0;
          clearPendingPermissionRetry();
          clearExpiryTimer();
          dispatch({ type: "REFRESH_FAILURE_401" });
          return;
        }
        if (status === 403) {
          deleteStoredRetryUntil();
          retryUntilRef.current = 0;
          clearPendingPermissionRetry();
          clearExpiryTimer();
          dispatch({ type: "REFRESH_FAILURE_403" });
          return;
        }
        if (isRetryableTransient) {
          const resolverErrorClass = isNetworkError || isTimeout || isNetworkCode ? "network" : "5xx";
          dispatch({ type: "REFRESH_FAILURE", now: Date.now(), resolverError: resolverErrorClass });
          const s = permStateRef.current;
          if (s.permissionsVerifiedAt != null) {
            const age = Date.now() - s.permissionsVerifiedAt;
            const remaining = Math.max(0, PERMISSION_FRESHNESS_MS - age);
            clearExpiryTimer();
            expiryTimerRef.current = setTimeout(() => {
              if (!mountedRef.current) return;
              dispatch({ type: "FRESHNESS_EXPIRED" });
            }, remaining);
          }
          // Arm exactly one bounded auto-retry unless we are the retry itself
          // or a cooldown was established meanwhile.
          if (!_isRetry
              && pendingRetryRef.current === null
              && !(retryUntilRef.current > 0 && Date.now() < retryUntilRef.current)) {
            armTransientRetry(myGen);
          }
          return;
        }

        // Non-retryable, non-401/403/429 (e.g., 500/501/other 5xx not
        // included in the retryable set, opaque error) — legacy behavior
        // with stillFresh grace; no auto-retry armed.
        dispatch({ type: "REFRESH_FAILURE", now: Date.now(), resolverError: "5xx" });
        const s = permStateRef.current;
        if (s.permissionsVerifiedAt != null) {
          const age = Date.now() - s.permissionsVerifiedAt;
          const remaining = Math.max(0, PERMISSION_FRESHNESS_MS - age);
          clearExpiryTimer();
          expiryTimerRef.current = setTimeout(() => {
            if (!mountedRef.current) return;
            dispatch({ type: "FRESHNESS_EXPIRED" });
          }, remaining);
        }
      } finally {
        if (inflightRef.current === promise) inflightRef.current = null;
      }
    })();

    inflightRef.current = promise;
    return promise;
  }, [clearExpiryTimer, clearPendingPermissionRetry, armTransientRetry]);

  // Keep the ref pointing at the latest refreshPermissions so the timer
  // callback can invoke the current function without a stale closure.
  refreshPermissionsRef.current = refreshPermissions;

  const invalidatePermissions = useCallback((options = {}) => {
    if (!mountedRef.current) return;
    genRef.current += 1;
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    inflightRef.current = null;
    clearExpiryTimer();
    clearPendingPermissionRetry();
    // NOTE: intentionally do NOT clear cooldown storage/ref during
    // invalidation. A permission-set invalidation is not the server
    // telling us the rate-limit ended.
    dispatch({ type: "INVALIDATE" });
    if (options && options.refresh) {
      void refreshPermissions({ force: true, reason: options.reason });
    }
  }, [clearExpiryTimer, clearPendingPermissionRetry, refreshPermissions]);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (!mountedRef.current) return;
      setUser(data);
      await refreshPermissions();
    } catch {
      if (!mountedRef.current) return;
      // US 1.9-B correction: an unauthenticated determination must clear
      // any prior identity-bound permission state (including a mount-
      // resumed cooldown) BEFORE the login flow is presented, so that a
      // subsequently signing-in different account cannot inherit the
      // previous session's cooldown, permissions, error, retry timer, or
      // in-flight request. This does NOT weaken the active-cooldown rule
      // for a still-authenticated session — it applies only when the
      // application has concluded the prior authenticated session is no
      // longer usable.
      deleteStoredRetryUntil();
      retryUntilRef.current = 0;
      clearPendingPermissionRetry();
      genRef.current += 1;
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* noop */ }
      }
      inflightRef.current = null;
      clearExpiryTimer();
      clearPollTimer();
      // HC-EPIC-002 · Slice 2D: reset current-user-refresh coordination
      // so that a subsequent authenticated session starts clean and does
      // not treat its first /permissions/me success as a "change".
      initialPermissionsLoadedRef.current = false;
      userRefreshInflightRef.current = null;
      dispatch({ type: "LOGOUT" });
      setUser(false);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [refreshPermissions, clearExpiryTimer, clearPollTimer, clearPendingPermissionRetry]);

  useEffect(() => {
    mountedRef.current = true;
    // US 1.9-B: hydrate persisted cooldown BEFORE checkSession() so that
    // the nested refreshPermissions() call inside checkSession() sees the
    // cooldown gate as active and does not issue /permissions/me.
    // /auth/me is NOT rate-limit-restricted here and proceeds normally.
    const stored = readStoredRetryUntil();
    if (stored !== null && stored > Date.now()) {
      retryUntilRef.current = stored;
      dispatch({ type: "MOUNT_RESUMED_COOLDOWN", retryUntil: stored });
    }
    checkSession();
    return () => {
      mountedRef.current = false;
      genRef.current += 1;
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* noop */ }
      }
      clearExpiryTimer();
      clearPollTimer();
      clearPendingPermissionRetry();
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.mfa_required) {
        return {
          ok: true,
          mfa_required: true,
          mfa_action: data.mfa_action,
          temp_token: data.temp_token,
        };
      }
      if (data?.access_token) setStoredToken(data.access_token);
      const { access_token, refresh_token, token_type, ...userData } = data;
      setUser(userData);
      await refreshPermissions({ force: true });
      return { ok: true, landing_route: data?.landing_route || null };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, [refreshPermissions]);

  const completeMfa = useCallback((data) => {
    if (data?.access_token) setStoredToken(data.access_token);
    const { access_token, refresh_token, token_type, ...userData } = data;
    setUser(userData);
    void refreshPermissions({ force: true });
    return data?.landing_route || null;
  }, [refreshPermissions]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing local session anyway.", e);
    }
    clearStoredToken();
    // US 1.9-B: explicit logout clears cooldown storage + ref + reducer.
    deleteStoredRetryUntil();
    retryUntilRef.current = 0;
    genRef.current += 1;
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    inflightRef.current = null;
    clearExpiryTimer();
    clearPollTimer();
    clearPendingPermissionRetry();
    focusLastRef.current = 0;
    // HC-EPIC-002 · Slice 2D: reset current-user-refresh coordination so
    // a subsequent login starts from a clean initial-load state.
    initialPermissionsLoadedRef.current = false;
    userRefreshInflightRef.current = null;
    setUser(false);
    dispatch({ type: "LOGOUT" });
  }, [clearExpiryTimer, clearPollTimer, clearPendingPermissionRetry]);

  useEffect(() => {
    if (!user || user === false) return undefined;

    const onFocus = () => {
      const now = Date.now();
      if (now - focusLastRef.current < PERMISSION_FOCUS_DEBOUNCE_MS) return;
      focusLastRef.current = now;
      void refreshPermissions();
    };

    const startPoll = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(() => {
        if (!mountedRef.current) return;
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
        void refreshPermissions();
      }, PERMISSION_POLL_MS);
    };

    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        void refreshPermissions();
        startPoll();
      } else {
        clearPollTimer();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    startPoll();

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      clearPollTimer();
    };
  }, [user, refreshPermissions, clearPollTimer]);

  useEffect(() => {
    const unregister = registerPermissionRevalidationHandler(() => {
      if (!mountedRef.current) return;
      void refreshPermissions({ force: true, reason: "backend_403" });
    });
    return () => { unregister(); };
  }, [refreshPermissions]);

  const permissionsFresh = useMemo(() => {
    if (permState.permissionState === "ready") return true;
    // HC-EPIC-002 · Phase 2E-A · Background-Refresh Grace
    // ------------------------------------------------------------------
    // Fix for P2E-EXEC-OPS-001: a background /permissions/me refresh
    // triggered by tab focus, poll, or visibilitychange must NOT
    // synchronously flip an authenticated user to /access-denied while
    // the refresh is in flight. Previously this memo bounded the fresh-
    // grace to `Date.now() - permissionsVerifiedAt < PERMISSION_FRESHNESS_MS`;
    // when a user returned to an inactive tab after 60s, the cached
    // permissionsVerifiedAt was already older than the window, so
    // REFRESH_STARTED (which sets state → "refreshing" while cached
    // permissions are non-null) caused `permissionsFresh` to flip to
    // false in the same render, forcing ProtectedRoute row 6 to
    // false-deny before the refresh returned.
    //
    // Corrective rule (zero-widening):
    //   • Trust the non-null cached permission set for the duration of
    //     an in-flight refresh, regardless of the cached set's age.
    //   • A permission NOT present in the cached set remains denied
    //     (`can()` still checks `perms.has(...)`).
    //   • Terminal failure is still fail-closed:
    //       - 401/403 → reducer clears `permissions` to null AND state
    //         → "error" with resolverError set → ProtectedRoute row 5
    //         emits a resolver_failure redirect.
    //       - Non-fresh 5xx/network → reducer sets state → "error"
    //         with resolverError → ProtectedRoute row 5 fires.
    //       - Successful refresh → new permission set replaces the
    //         cached one atomically via REFRESH_SUCCESS.
    //   • The reducer's FRESHNESS_EXPIRED transition remains untouched
    //     for a genuinely-hung refresh; when it fires (state==="refreshing"
    //     → "error") the memo returns false via the fall-through.
    if (permState.permissionState === "refreshing" && permState.permissions != null) {
      return true;
    }
    return false;
  }, [permState.permissionState, permState.permissions]);

  const permissionsLoaded = useMemo(() => {
    return permState.permissionState !== "idle" &&
           permState.permissionState !== "loading";
  }, [permState.permissionState]);

  const can = useCallback(
    (permission) => {
      if (typeof permission !== "string" || permission.trim() === "") return false;
      if (!user || user === false) return false;
      if (!permissionsFresh) return false;
      const perms = permState.permissions;
      if (!perms) return false;
      return perms.has(permission);
    },
    [user, permissionsFresh, permState.permissions],
  );

  const canSeeModule = useCallback(
    (moduleKey) => {
      if (typeof moduleKey !== "string" || moduleKey.trim() === "") return false;
      const permission = MODULE_PERMISSION_MAP[moduleKey];
      if (!permission) return false;
      return can(permission);
    },
    [can],
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      completeMfa,
      logout,
      refresh: checkSession,
      permissions: permState.permissions,
      permissionState: permState.permissionState,
      permissionsVerifiedAt: permState.permissionsVerifiedAt,
      permissionsFresh,
      refreshPermissions,
      invalidatePermissions,
      permissionsLoaded,
      can,
      canSeeModule,
      // US 1.9-B additions (stable data only; no time-reactive booleans).
      permissionRetryAt: permState.retryUntil,
      permissionResolverError: permState.resolverError,
    }),
    [
      user, loading, login, completeMfa, logout, checkSession,
      permState.permissions, permState.permissionState, permState.permissionsVerifiedAt,
      permState.retryUntil, permState.resolverError,
      permissionsFresh, refreshPermissions, invalidatePermissions,
      permissionsLoaded, can, canSeeModule,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

// ---------------------------------------------------------------------------
// Legacy helper — retained ONLY for existing callers. Reads user.role
// directly and MUST NOT be used for new authorization decisions.
// Sprint 1B-B · US 1.7 will remove all remaining consumers.
// ---------------------------------------------------------------------------
export function canManage(user, modules) {
  if (!user) return false;
  if (user.role === "admin") return true;
  const map = {
    users: ["admin"],
    care_seekers: ["admin", "executive_director", "field_specialist", "care_navigator"],
    referrals: ["admin", "executive_director", "care_navigator", "field_specialist"],
    reports: ["admin", "executive_director"],
    maternal: ["admin", "executive_director", "field_specialist", "care_navigator"],
  };
  return (map[modules] || []).includes(user.role);
}
